import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";

const SlackContext = createContext(null);

export function SlackProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef(null);
  const listenersRef = useRef(new Set());
  const viewingRef = useRef(false);

  const subscribe = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const setViewing = useCallback((val) => {
    viewingRef.current = val;
    if (val) setUnreadCount(0);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sprintai_token");
    if (!token) return;

    const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const sseUrl = `${backendUrl}/api/slack/stream?token=${token}`;

    const connect = () => {
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if ((data.type === "new_message" || data.type === "thread_reply" || data.type === "reaction_added") && (data.message || data.reaction)) {
            listenersRef.current.forEach(cb => cb(data));
            if (!viewingRef.current) {
              setUnreadCount(prev => prev + 1);
            }
          }
        } catch {}
      };

      es.onerror = () => {
        es.close();
        setTimeout(() => {
          if (eventSourceRef.current === es) connect();
        }, 5000);
      };
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return (
    <SlackContext.Provider value={{ unreadCount, clearUnread, subscribe, setViewing }}>
      {children}
    </SlackContext.Provider>
  );
}

export const useSlack = () => useContext(SlackContext) || { unreadCount: 0, clearUnread: () => {}, subscribe: () => () => {}, setViewing: () => {} };
